import { type ReactNode, useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import * as updatedProductStore from '@cartwhl/common/stores/updatedProductStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import { getAppletsUrl, getHomepageUrl } from '@cartwhl/common/utils';
import {
  formatOrgCreditsForDisplay,
  getOrgAvailableCredits,
  isActiveFreeTrial,
  isOrgCreditsWorkspace,
} from '@cartwhl/common/utils/organizationWorkspace';
import Avatar from '@cartwhl/ui/atoms/Avatar';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Text from '@cartwhl/ui/base/TextNew';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import HomeIcon from '@cartwhl/ui/icons/HomeIcon';
import LightDarkIcon from '@cartwhl/ui/icons/LightDarkIcon';
import LogoutIcon from '@cartwhl/ui/icons/LogoutIcon';
import PenIcon from '@cartwhl/ui/icons/PenIcon';
import UserIcon from '@cartwhl/ui/icons/UserIcon';

import SpinIcon from '../icons/SpinIcon';
import Button from '../input/Button';

const USER_MENU_DROPDOWN_Z_INDEX = 60;
type SpinsExplainedModalComponent =
  typeof import('../modals/SpinsExplainedModal').default;

let spinsExplainedModalImport:
  | Promise<{ default: SpinsExplainedModalComponent }>
  | undefined;

function importSpinsExplainedModal() {
  spinsExplainedModalImport ??= import('../modals/SpinsExplainedModal').catch(
    (error) => {
      spinsExplainedModalImport = undefined;
      throw error;
    },
  );
  return spinsExplainedModalImport;
}

interface AccountCreditsLabelProps {
  align?: 'end' | 'start';
  className?: string;
  creditsLabel: string;
  secondaryLabel: string;
}

export function AccountCreditsAmount({
  creditsLabel,
}: {
  creditsLabel: string;
}) {
  return (
    <Text token="14/medium" leading="title">
      {creditsLabel}
    </Text>
  );
}

export function AccountCreditsSecondaryLabel({
  className = '',
  secondaryLabel,
}: {
  className?: string;
  secondaryLabel: string;
}) {
  return (
    <Text token="14/medium" className={`text-tertiary truncate ${className}`}>
      {secondaryLabel}
    </Text>
  );
}

export function AccountCreditsLabel({
  align = 'end',
  className = '',
  creditsLabel,
  secondaryLabel,
}: AccountCreditsLabelProps) {
  return (
    <span
      className={`flex min-w-0 flex-col gap-0.5 ${
        align === 'end' ? 'items-end' : 'items-start'
      } ${className}`}
    >
      <AccountCreditsAmount creditsLabel={creditsLabel} />
      <AccountCreditsSecondaryLabel
        className="max-w-40"
        secondaryLabel={secondaryLabel}
      />
    </span>
  );
}

interface UserMenuProps {
  inModal?: boolean;
  showThemeSelector?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  /**
   * Replaces the default circular avatar trigger. Must be a single element
   * Radix can merge into (e.g. `<button type="button">`).
   */
  trigger?: ReactNode;
}

export default function UserMenu({
  inModal = false,
  showThemeSelector = true,
  side = 'bottom',
  align = 'end',
  onOpenChange,
  open,
  trigger: triggerOverride,
}: UserMenuProps) {
  const { userInfo, theme, organizationInfo } = useSnapshot(userStore.state);
  const [spinsExplainedOpen, setSpinsExplainedOpen] = useState(false);
  const [SpinsExplainedModal, setSpinsExplainedModal] =
    useState<SpinsExplainedModalComponent | null>(null);
  const [spinsExplainedLoadError, setSpinsExplainedLoadError] = useState(false);

  useEffect(() => {
    if (!spinsExplainedOpen || SpinsExplainedModal) return;
    void loadSpinsExplainedModal();
  }, [spinsExplainedOpen, SpinsExplainedModal]);

  function loadSpinsExplainedModal() {
    setSpinsExplainedLoadError(false);
    return importSpinsExplainedModal()
      .then(({ default: ModalComponent }) => {
        setSpinsExplainedModal(() => ModalComponent);
      })
      .catch(() => {
        setSpinsExplainedLoadError(true);
      });
  }

  const orgCreditsWorkspace = isOrgCreditsWorkspace(organizationInfo);
  const orgAvailableCredits = getOrgAvailableCredits(organizationInfo);

  const defaultTrigger = (
    <button
      type="button"
      aria-label="Account menu"
      className="hover:bg-gray-15 data-[state=open]:bg-gray-15 hover:dark:bg-gray-75 data-[state=open]:dark:bg-gray-90 outline-hidden flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 transition-colors"
    >
      <Avatar userInfo={userInfo} />
    </button>
  );

  return (
    <>
      <Dropdown
        inModal={inModal}
        side={side}
        align={align}
        zIndex={inModal ? undefined : USER_MENU_DROPDOWN_Z_INDEX}
        open={open}
        onOpenChange={onOpenChange}
        trigger={triggerOverride ?? defaultTrigger}
      >
        <div className="styled-scrollbar max-h-[calc(var(--radix-dropdown-menu-content-available-height)-1rem)] overflow-y-auto overflow-x-hidden overscroll-contain">
          <div className="hover:bg-gray-15 hover:dark:bg-gray-90 m-2 h-[120px] rounded-md">
            <a
              className="relative flex h-full w-full flex-col items-center justify-center"
              href={getHomepageUrl('/profile/account-details')}
            >
              <Avatar userInfo={userInfo} size={12} className="relative mb-3">
                <span className="dark:bg-gray-90 absolute -bottom-1 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-white">
                  <PenIcon />
                </span>
              </Avatar>
              <Text token="14/medium">
                {userInfo.firstName} {userInfo.lastName}
              </Text>
              <Text token="12" className="text-gray-45">
                {userInfo.email}
              </Text>
              <Text token="12" className="text-gray-45">
                {userInfo.planType}
              </Text>
            </a>
          </div>
          <div className="flex items-center justify-center">
            <a
              href={getHomepageUrl('/profile/plan-billing')}
              className="border-primary hover:bg-gray-15 dark:hover:bg-gray-90 inline-flex h-10 items-center gap-2 rounded-md border px-3 py-2 transition-colors"
            >
              <SpinIcon className="h-5 w-5 shrink-0" />
              <AccountCreditsAmount
                creditsLabel={
                  orgCreditsWorkspace
                    ? `${formatOrgCreditsForDisplay(orgAvailableCredits)} credits`
                    : `${userInfo.spinsAvailable ?? 0} Spins`
                }
              />
            </a>
          </div>
          <div className="mb-3 mt-2 flex items-center justify-center">
            {orgCreditsWorkspace ? (
              <a
                href={getHomepageUrl('/pricing#pricing-credits-explainer')}
                className="text-gray-45 underline"
              >
                <Text token="12">How credits work</Text>
              </a>
            ) : (
              <button
                className="text-gray-45 cursor-pointer underline"
                onClick={() => {
                  setSpinsExplainedOpen(true);
                  void loadSpinsExplainedModal();
                }}
              >
                <Text token="12">What are Spins?</Text>
              </button>
            )}
          </div>
          {isActiveFreeTrial(organizationInfo) && (
            <>
              {/* <Dropdown.Item
              disabled={payNowLoading}
              icon={
                <BlueDotIcon className="text-blue transition-colors group-hover:text-white" />
              }
              variant="blue"
              onClick={async () => {
                if (payNowLoading) return;
                setPayNowLoading(true);
                try {
                  await updatedProductStore.completeStripeTrialEarly();
                } finally {
                  setPayNowLoading(false);
                }
              }}
            >
              {payNowLoading ? 'Processing…' : 'Upgrade'}
            </Dropdown.Item> */}
              <Button
                variant="primary"
                className="w-full"
                onClick={() => {
                  updatedProductStore.openActivateEarlyTrialModal();
                }}
              >
                Activate now
              </Button>
            </>
          )}
          {/* {userInfo.planType !== 'unlimited' && (
          <>
            <Dropdown.Item
              href={getHomepageUrl('/pricing')}
              icon={
                <BlueDotIcon className="text-blue transition-colors group-hover:text-white" />
              }
              variant="blue"
            >
              {userInfo.planType === 'free'
                ? 'Upgrade'
                : userInfo.planType === 'pro'
                  ? 'Upgrade to Advanced'
                  : 'Unlimited Plan'}
            </Dropdown.Item>

            <Dropdown.Separator />
          </>
        )} */}
          <Dropdown.Separator />
          <Dropdown.Item
            href={getAppletsUrl()}
            icon={<HomeIcon className="h-5 w-5" />}
          >
            Dashboard
          </Dropdown.Item>
          <Dropdown.Item
            href={getHomepageUrl('/profile')}
            icon={<UserIcon className="h-5 w-5" />}
          >
            Account & Billing
          </Dropdown.Item>
          {showThemeSelector && (
            <Dropdown.Sub
              text="Theme"
              icon={<LightDarkIcon className="h-5 w-5" />}
              zIndex={inModal ? undefined : USER_MENU_DROPDOWN_Z_INDEX}
            >
              <Dropdown.Item
                icon={theme === 'light' ? <CheckmarkIcon /> : null}
                onClick={() => userStore.setTheme('light')}
              >
                Light
              </Dropdown.Item>
              <Dropdown.Item
                icon={theme === 'dark' ? <CheckmarkIcon /> : null}
                onClick={() => userStore.setTheme('dark')}
              >
                Dark
              </Dropdown.Item>
              <Dropdown.Item
                icon={theme === null ? <CheckmarkIcon /> : null}
                onClick={() => userStore.setTheme(null)}
              >
                System
              </Dropdown.Item>
            </Dropdown.Sub>
          )}
          <Dropdown.Separator />
          <Dropdown.Item href={getHomepageUrl('/logout')} icon={<LogoutIcon />}>
            Logout
          </Dropdown.Item>
        </div>
      </Dropdown>
      {spinsExplainedOpen &&
        (SpinsExplainedModal ? (
          <SpinsExplainedModal
            open={spinsExplainedOpen}
            onOpenChange={setSpinsExplainedOpen}
          />
        ) : (
          <div
            className="bg-primary border-primary fixed bottom-4 right-4 z-[10001] flex max-w-[calc(100vw-2rem)] items-center gap-3 rounded-lg border p-3 shadow-menu"
            role={spinsExplainedLoadError ? 'alert' : 'status'}
            aria-live="polite"
          >
            <Text token="14/medium">
              {spinsExplainedLoadError
                ? 'Unable to load Spins details.'
                : 'Loading Spins details…'}
            </Text>
            {spinsExplainedLoadError && (
              <Button variant="secondary" onClick={() => void loadSpinsExplainedModal()}>
                Retry
              </Button>
            )}
            <Button variant="secondary" onClick={() => setSpinsExplainedOpen(false)}>
              Close
            </Button>
          </div>
        ))}
    </>
  );
}
